import { Injectable } from '@angular/core';
import { GoogleAnalyticsService } from './google-analytics.service';

export type AuthMethod = 'google' | 'email';

/** Placement reported when no paywall opened the gate (the /sign-up page). */
export const NO_GATE_PLACEMENT = 'direct';

/**
 * Reports the moment an account is actually created, which nothing in the
 * app used to do: every existing auth event is a click on the way there
 * (`create_account_click` fires before validation, `sign_in_email` means
 * "chose email over Google"), so none of them can be used as a conversion.
 *
 * Fired from AuthenticationService, the one place both providers converge,
 * rather than from each form — the modal's Google button, the register
 * form's own Google button and the sign-in page all reach the same two HTTP
 * calls, and a per-component event would either miss some of them or count
 * one sign-up twice.
 *
 * Placement is remembered separately because the gate that triggered the
 * sign-up is known to whoever opened it (ModalService.requestAuth) and long
 * forgotten by the time the account exists.
 */
@Injectable({ providedIn: 'root' })
export class SignUpTrackingService {
  private placement: string | null = null;

  constructor(private ga: GoogleAnalyticsService) {}

  /** A paywall opened the sign-up gate. */
  public gateOpened(placement: string): void {
    this.placement = placement;
  }

  /** The gate closed, signed up or not: later sign-ups are not its doing. */
  public gateClosed(): void {
    this.placement = null;
  }

  /**
   * The gate currently open, for the auth requests themselves: the server
   * writes the REGISTERED funnel row, so it needs the placement on the
   * request rather than after the fact.
   */
  public currentPlacement(): string | null {
    return this.placement;
  }

  /** A new account now exists. */
  public signedUp(method: AuthMethod): void {
    const placement = this.placement ?? NO_GATE_PLACEMENT;
    this.safely(() => this.ga.logSignUp(method, placement));
  }

  /**
   * An existing account logged in. Not a conversion, and deliberately not
   * reported as one: an Ads sign-up conversion that also counted returning
   * users would bid toward traffic that already converted.
   */
  public signedIn(method: AuthMethod): void {
    this.safely(() => this.ga.logLogin(method));
  }

  private safely(report: () => void): void {
    // Tracking must never break an auth flow the user is in the middle of.
    try {
      report();
    } catch (err) {
      console.error('[sign-up-tracking] reporting failed', err);
    }
  }
}
